import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Sparkles, Circle, Square, Triangle, Linkedin, Instagram } from "lucide-react";
import AIConciergeWidget from "@/components/AIConciergeWidget";
import DemoTopNav from "@/components/demos/DemoTopNav";
import { DEMO_THEMES } from "@/data/demoThemes";

const INK = "#141414";
const BLUE = "#2563EB";
const PAPER = "#F6F5F1";
const display = { fontFamily: "'Playfair Display', serif" };
const body = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

const IMG = {
  portrait: "https://images.unsplash.com/photo-1594744803329-e58b31de8bf5?crop=entropy&cs=srgb&fm=jpg&w=800&q=80&ixlib=rb-4.1.0",
  w1: "https://images.unsplash.com/photo-1621111848501-8d3634f82336?crop=entropy&cs=srgb&fm=jpg&w=600&q=80&ixlib=rb-4.1.0",
  w2: "https://images.unsplash.com/photo-1760022638435-aad7c1e684b6?crop=entropy&cs=srgb&fm=jpg&w=600&q=80&ixlib=rb-4.1.0",
  w3: "https://images.unsplash.com/photo-1768471125958-78556538fadc?crop=entropy&cs=srgb&fm=jpg&w=600&q=80&ixlib=rb-4.1.0",
};
const WORK = [
  { img: IMG.w1, t: "Northstar Health", tag: "Product · 2023", note: "A calmer approach to care." },
  { img: IMG.w2, t: "Fieldnote", tag: "Brand · 2022", note: "People. Places. Progress." },
  { img: IMG.w3, t: "Common Ground", tag: "Strategy · 2024", note: "Belonging builds brighter cities." },
];
const EXP = [
  { y: "2021 — Present", r: "Independent Product Designer & Strategist" },
  { y: "2018 — 2021", r: "Senior Product Designer" },
  { y: "2016 — 2018", r: "Product Designer" },
];
const CAPS = [
  { icon: Circle, t: "Product Strategy" },
  { icon: Square, t: "UX & Interface Design" },
  { icon: Triangle, t: "Design Systems" },
  { icon: Circle, t: "Creative Direction" },
];
const NOTES = [
  { t: "Designing for mental health in a distracted world", d: "MAR 12, 2025 · 6 MIN READ" },
  { t: "The creative advantage in an AI-powered future", d: "JAN 28, 2025 · 8 MIN READ" },
];
const openChat = () => window.dispatchEvent(new CustomEvent("concierge:open"));
const openBook = () => window.dispatchEvent(new CustomEvent("concierge:book"));

export default function AmaraValeDemo() {
  useEffect(() => { window.scrollTo(0, 0); document.title = "Amara Vale (Demo) | WebsiteTech.pro"; }, []);

  return (
    <div className="min-h-screen" style={{ background: PAPER, color: INK, ...body }} data-testid="portfolio-demo">
      <Link to="/demos" className="block w-full text-center text-[11px] tracking-widest uppercase py-1.5 text-white" style={{ background: INK }}>
        WebsiteTech.pro Demo · Portfolio — click to view all industry demos
      </Link>

      <DemoTopNav
        testid="portfolio-nav"
        logo={<div className="text-lg font-extrabold tracking-tight" style={{ color: INK }}>AMARA VALE</div>}
        items={["Work", "About", "Experience", "Journal", "Contact"]}
        ctaLabel="Book a Call"
        onCta={openBook}
        theme={{ bg: "rgba(246,245,241,0.94)", text: INK, accent: "#D9F24B", accentText: INK, border: "#e4e2da" }}
      />

      {/* hero */}
      <header data-testid="portfolio-hero" className="max-w-7xl mx-auto px-6 pt-10 grid lg:grid-cols-[1.1fr_0.9fr] gap-8">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase opacity-50">Ideas · People · Better Tomorrows</div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="mt-4 text-6xl sm:text-7xl lg:text-8xl font-extrabold leading-[0.9] tracking-tight">AMARA<br />VALE</motion.h1>
          <p className="mt-6 text-2xl" style={display}>Product designer. Creative strategist. Curious human.</p>
          <p className="mt-3 text-base opacity-70 max-w-md">I turn complex ideas into clear, useful experiences.</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="#work" className="rounded-full px-6 py-3 text-xs tracking-[0.15em] uppercase font-semibold text-white flex items-center gap-2" style={{ background: BLUE }}>View Selected Work <ArrowRight size={14} /></a>
            <button onClick={openChat} data-testid="portfolio-ask-resume" className="rounded-full px-6 py-3 text-xs tracking-[0.15em] uppercase font-semibold flex items-center gap-2" style={{ border: `1px solid ${INK}`, color: INK }}><Sparkles size={14} /> Ask My AI Résumé</button>
          </div>
        </div>
        <div className="relative">
          <img src={IMG.portrait} alt="Amara Vale" className="rounded-2xl h-[420px] w-full object-cover" />
          <div className="mt-4 rounded-2xl border bg-white p-5" style={{ borderColor: "#e4e2da" }} data-testid="portfolio-availability">
            <div className="flex items-center gap-2 text-xs"><span className="h-2 w-2 rounded-full bg-green-500" /> Currently accepting select projects</div>
            <div className="mt-2 text-2xl" style={display}>Let's make something useful.</div>
            <button onClick={openBook} data-testid="portfolio-check-availability" className="mt-3 w-full rounded-full px-5 py-3 text-xs tracking-[0.15em] uppercase font-semibold text-white flex items-center justify-center gap-2" style={{ background: INK }}>Check Availability <ArrowRight size={14} /></button>
          </div>
        </div>
      </header>

      {/* selected work */}
      <section id="work" data-testid="portfolio-work" className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex items-end justify-between mb-8"><span className="text-xs tracking-[0.25em] uppercase font-bold opacity-50">Selected Work</span><span className="text-xs tracking-widest uppercase" style={{ color: BLUE }}>See all work →</span></div>
        <div className="grid md:grid-cols-3 gap-6">
          {WORK.map((w) => (
            <div key={w.t} className="group cursor-pointer" data-testid="portfolio-work-item">
              <div className="relative rounded-xl overflow-hidden aspect-[4/3]">
                <img src={w.img} alt={w.t} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <span className="absolute bottom-3 left-3 right-3 text-white text-sm italic" style={{ ...display, textShadow: "0 1px 6px rgba(0,0,0,0.6)" }}>{w.note}</span>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <div><div className="font-bold">{w.t}</div><div className="text-[11px] uppercase tracking-widest opacity-50">{w.tag}</div></div>
                <ArrowUpRight size={16} className="opacity-40 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* about / experience / notes */}
      <section className="max-w-7xl mx-auto px-6 pb-20 grid md:grid-cols-3 gap-10 border-t pt-12" style={{ borderColor: "#e4e2da" }}>
        <div data-testid="portfolio-about">
          <span className="text-xs tracking-[0.25em] uppercase font-bold opacity-50">About Amara</span>
          <p className="mt-4 text-sm opacity-75 leading-relaxed">I'm a multidisciplinary product designer and creative strategist who cares about people, progress, and the quiet power of good design. I partner with mission-driven teams to turn big, complex ideas into clear, human-centered experiences.</p>
          <button onClick={openChat} className="mt-4 text-sm font-semibold inline-flex items-center gap-1" style={{ color: BLUE }}>Read more <ArrowRight size={14} /></button>
        </div>
        <div data-testid="portfolio-experience">
          <span className="text-xs tracking-[0.25em] uppercase font-bold opacity-50">Experience & Capabilities</span>
          <div className="mt-4 space-y-3">
            {EXP.map((e) => (<div key={e.y}><div className="text-[11px] uppercase tracking-widest opacity-50">{e.y}</div><div className="text-sm font-medium">{e.r}</div></div>))}
          </div>
          <div className="mt-5 grid grid-cols-2 gap-2">
            {CAPS.map((c) => (<div key={c.t} className="flex items-center gap-2 text-sm"><c.icon size={13} style={{ color: BLUE }} /> {c.t}</div>))}
          </div>
        </div>
        <div data-testid="portfolio-notes">
          <span className="text-xs tracking-[0.25em] uppercase font-bold opacity-50">Selected Notes</span>
          <div className="mt-4 space-y-4">
            {NOTES.map((n) => (<div key={n.t} className="cursor-pointer group"><div className="text-base leading-snug group-hover:opacity-70 transition-opacity" style={display}>{n.t}</div><div className="text-[11px] uppercase tracking-widest opacity-50 mt-1">{n.d}</div></div>))}
          </div>
          <button onClick={openChat} className="mt-4 text-sm font-semibold inline-flex items-center gap-1" style={{ color: BLUE }}>View all notes <ArrowRight size={14} /></button>
        </div>
      </section>

      <footer data-testid="portfolio-footer" className="border-t py-8" style={{ borderColor: "#e4e2da" }}>
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap items-center justify-between gap-4 text-sm">
          <div className="font-extrabold tracking-tight">AMARA VALE</div>
          <p className="opacity-60">Product design for a kinder, more human tomorrow.</p>
          <div className="flex gap-3 opacity-60"><Linkedin size={16} /><Instagram size={16} /></div>
        </div>
      </footer>

      <AIConciergeWidget demo="portfolio" concierge={DEMO_THEMES.portfolio.concierge} />
    </div>
  );
}
